import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@utils/useApi";
import {
  EcommerceShoppingCartClient,
  QuoteCartLineSelectionDto,
} from "@lib/ShiOneClient";
import { invalidateCart } from "@features/ecommerce/cart/cartQueryKeys";
import type { BuyAgainResult } from "@features/ecommerce/components/BuyAgainResultDialog";

/** SPU-456 B4: one selected quote line + the customer-adjusted quantity for a partial add-to-cart. */
export interface QuoteLineSelection {
  lineId: number;
  quantity: number;
}

/**
 * Either a bare quoteId (whole-quote add — every existing caller) or, for the detail page's line
 * selection, an object carrying the quoteId plus the selected lines. A null/empty lineSelection means
 * "add the whole quote" and is only honored for active quotes (the backend ignores it for expired).
 */
export type AddQuoteToCartVariables =
  | number
  | { quoteId: number; lineSelection?: QuoteLineSelection[] };

const toQuoteId = (vars: AddQuoteToCartVariables): number =>
  typeof vars === "number" ? vars : vars.quoteId;

export function useAddQuoteToCart() {
  const api = useApi(EcommerceShoppingCartClient);
  const queryClient = useQueryClient();
  // Local state, not the routed buy-again dialog: its `{page}/buy-again-result` segment collides
  // with `/quotes/:quoteId` and navigates to a "quote not found" page.
  const [failureResult, setFailureResult] = useState<BuyAgainResult | null>(null);
  const [pendingQuoteIds, setPendingQuoteIds] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const mutation = useMutation({
    mutationFn: async (vars: AddQuoteToCartVariables) => {
      const quoteId = toQuoteId(vars);
      const lineSelection =
        typeof vars === "number" ? undefined : vars.lineSelection;
      setPendingQuoteIds((prev) => new Set(prev).add(quoteId));
      // SPU-456 B4: a non-empty selection adds only those lines at the chosen quantities (active quotes
      // only — the backend ignores it for expired). An undefined body keeps the whole-quote add.
      const body = lineSelection?.length
        ? lineSelection.map((s) => new QuoteCartLineSelectionDto(s))
        : undefined;
      return await api.addQuoteItemsToShoppingCart(quoteId, body);
    },
    onSuccess: async (data, vars) => {
      const quoteId = toQuoteId(vars);
      setPendingQuoteIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(quoteId);
        return newSet;
      });
      await invalidateCart(queryClient);
      // Lines the catalog can't supply are skipped server-side; report them instead of a false success.
      const failedCount = data?.failedCount ?? 0;
      if (data && failedCount > 0) {
        setFailureResult({
          successCount: data.successCount ?? 0,
          failedCount,
          failedItems: (data.failedItems ?? []).map((f) => ({
            productDescriptor: f.productDescriptor ?? "",
            productName: f.productName ?? "",
            reason: f.reason ?? "",
          })),
        });
        return;
      }
      setSnackbar({
        open: true,
        message: "Quote items added to cart successfully!",
        severity: "success",
      });
    },
    onError: (_error, vars) => {
      const quoteId = toQuoteId(vars);
      setPendingQuoteIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(quoteId);
        return newSet;
      });
      setSnackbar({
        open: true,
        message: "Failed to add quote items to cart. Please try again.",
        severity: "error",
      });
    },
  });

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return {
    mutation,
    pendingQuoteIds,
    snackbar,
    closeSnackbar,
    resultDialogProps: {
      open: failureResult != null,
      onClose: () => setFailureResult(null),
      result: failureResult,
    },
  };
}
